import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

interface ContentGenerationPayload {
  idea_id: number;
  idea_type: string;
  idea: string;
  task_type: string;
  campaign_goal: string;
  quarterly_budget: string;
  weekly_promotional_goal: string;
  blog_post_goal: string;
  target_keyword: string;
}

interface SocialMediaContent {
  content_id: number;
  source_idea_id: number;
  content_type: string;
  content_payload: string;
  primary_channel: string;
  status: string;
  created_at: string;
  version: number;
  idea_type: string;
}

interface SeasonalFlyerContent {
  content_id: number;
  source_idea_id: number;
  content_type: string;
  content_payload: string;
  primary_channel: string;
  status: string;
  created_at: string;
  version: number;
  idea_type: string;
}

interface ContentGenerationResponse {
  social_media: SocialMediaContent[];
  seasonal_flyer: SeasonalFlyerContent[];
}

interface FlyerDetails {
  id: number;
  created_at: string;
  flyer_url: string;
  content_id: number;
  content_type: string;
  version: number;
  video_url: string;
}

interface ContentGenerationState {
  loading: boolean;
  error: string | null;
  generatedContent: ContentGenerationResponse | null;
  flyerDetails: FlyerDetails | null;
  contentId: number | null;
  ideaId: number | null;
  ideaType: string | null;
}

const initialState: ContentGenerationState = {
  loading: false,
  error: null,
  generatedContent: null,
  flyerDetails: null,
  contentId: null,
  ideaId: null,
  ideaType: null,
};

const contentGenerationSlice = createSlice({
  name: 'contentGeneration',
  initialState,
  reducers: {
    triggerContentGenerationRequest: (
      state,
      _action: PayloadAction<{ restaurantId: string; payload: ContentGenerationPayload }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    triggerContentGenerationSuccess: (
      state,
      action: PayloadAction<any>
    ) => {
      state.generatedContent = action.payload;
      // Extract content_id from seasonal_flyer if available
      if (action.payload.seasonal_flyer && action.payload.seasonal_flyer.length > 0) {
        state.contentId = action.payload.seasonal_flyer[0].content_id;
      }
      // Store idea_id and idea_type for task monitoring
      state.ideaId = action.payload.idea_id;
      state.ideaType = action.payload.idea_type;
    },
    triggerContentGenerationFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    fetchFlyerDetailsSuccess: (state, action: PayloadAction<FlyerDetails>) => {
      state.loading = false;
      state.flyerDetails = action.payload;
      state.error = null;
    },
    fetchFlyerDetailsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    resetContentGeneration: (state) => {
      state.loading = false;
      state.error = null;
      state.generatedContent = null;
      state.flyerDetails = null;
      state.contentId = null;
      state.ideaId = null;
      state.ideaType = null;
    },
  },
});

export const {
  triggerContentGenerationRequest,
  triggerContentGenerationSuccess,
  triggerContentGenerationFailure,
  fetchFlyerDetailsSuccess,
  fetchFlyerDetailsFailure,
  resetContentGeneration,
} = contentGenerationSlice.actions;

export default contentGenerationSlice.reducer;
